import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faExpand,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faCalendarAlt,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import '../styles/DashboardChart.css';

export type TimeInterval = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface ChartDataPoint {
  label: string;
  value: number;
  color?: string;
  meta?: Record<string, any>;
}

export interface ChartData {
  id: string;
  title: string;
  type: 'bar' | 'pie' | 'line' | 'number';
  data: ChartDataPoint[];
  description?: string;
  interval?: TimeInterval;
  currentPeriod?: Date;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'stable';
  };
}

interface DashboardChartProps {
  chart: ChartData;
  onDelete?: (id: string) => void;
  onIntervalChange?: (id: string, interval: TimeInterval) => void;
  onPeriodChange?: (id: string, direction: 'prev' | 'next') => void;
}

interface TooltipData {
  x: number;
  y: number;
  content: ChartDataPoint;
  visible: boolean;
}

const CHART_COLORS = [
  '#6366f1', // indigo
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#06b6d4', // cyan
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
  '#84cc16', // lime
  '#3b82f6', // blue
  '#d946ef', // fuchsia
  '#0ea5e9', // sky
  '#a855f7', // purple
  '#22d3ee', // light-cyan
  '#e11d48', // rose
  '#059669', // green-dark
  '#7c3aed', // violet-dark
  '#0284c7', // blue-dark
  '#ca8a04', // yellow-dark
  '#f472b6', // pink-light
  '#fb923c', // orange-light
  '#a3e635', // lime-light
  '#34d399', // emerald-light
  '#2dd4bf', // teal-light
  '#38bdf8', // sky-light
  '#818cf8', // indigo-light
  '#c084fc', // purple-light
  '#f0abfc', // fuchsia-light
  '#fda4af', // rose-light
];

const formatValue = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString();
};

const getPeriodLabel = (date: Date | undefined, interval: TimeInterval | undefined): string => {
  if (!date) return 'Current';
  
  const d = new Date(date);
  
  switch (interval) {
    case 'day':
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    case 'week':
      const weekStart = new Date(d);
      weekStart.setDate(d.getDate() - d.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    case 'month':
      return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    case 'quarter':
      const quarter = Math.floor(d.getMonth() / 3) + 1;
      return `Q${quarter} ${d.getFullYear()}`;
    case 'year':
      return d.getFullYear().toString();
    default:
      return 'Current Period';
  }
};

// Tooltip Component
const Tooltip: React.FC<{ data: TooltipData }> = ({ data }) => {
  if (!data.visible) return null;
  
  // Clamp position to stay within the chart container
  const tooltipWidth = 160;
  const tooltipHeight = 90;
  
  let left = Math.max(tooltipWidth / 2 + 4, Math.min(data.x, 600 - tooltipWidth / 2));
  let top = Math.max(10, data.y);
  
  // If too close to top, show below instead
  if (top < tooltipHeight) {
    top = data.y + 60;
  }
  
  return (
    <div
      className="chart-tooltip"
      style={{
        left,
        top,
        maxWidth: '200px',
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      <div className="tooltip-label">{data.content.label}</div>
      <div className="tooltip-value">{formatValue(data.content.value)}</div>
      {data.content.meta && (
        <div className="tooltip-meta">
          {Object.entries(data.content.meta).map(([key, val]) => (
            <div key={key} className="tooltip-meta-item">
              <span className="meta-key">{key}:</span>
              <span className="meta-value">{val}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Bar Chart Component
const BarChart: React.FC<{ data: ChartDataPoint[], onHover: (data: TooltipData) => void }> = ({ data, onHover }) => {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  const chartRef = useRef<SVGSVGElement>(null);
  
  const handleMouseEnter = (item: ChartDataPoint, event: React.MouseEvent) => {
    const rect = (event.target as SVGElement).getBoundingClientRect();
    const containerRect = chartRef.current?.getBoundingClientRect();
    if (containerRect) {
      onHover({
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.top - containerRect.top - 10,
        content: item,
        visible: true,
      });
    }
  };
  
  const handleMouseLeave = () => {
    onHover({ x: 0, y: 0, content: { label: '', value: 0 }, visible: false });
  };

  return (
    <svg ref={chartRef} viewBox="0 0 300 180" className="chart-svg bar-chart">
      {/* Y-axis gridlines */}
      {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
        <g key={i}>
          <line
            x1="40"
            y1={160 - ratio * 140}
            x2="290"
            y2={160 - ratio * 140}
            stroke="rgba(255,255,255,0.06)"
            strokeDasharray="4,4"
          />
          <text x="35" y={165 - ratio * 140} textAnchor="end" className="chart-axis-label">
            {formatValue(Math.round(maxValue * ratio))}
          </text>
        </g>
      ))}
      
      {/* Bars */}
      {data.slice(0, 8).map((item, index) => {
        const barWidth = Math.min(25, 200 / data.length);
        const gap = (250 - data.slice(0, 8).length * barWidth) / (data.slice(0, 8).length + 1);
        const x = 40 + gap + index * (barWidth + gap);
        const barHeight = (item.value / maxValue) * 140;
        const color = item.color || CHART_COLORS[index % CHART_COLORS.length];
        
        return (
          <g key={item.label} className="bar-group">
            <rect
              x={x}
              y={160 - barHeight}
              width={barWidth}
              height={barHeight}
              fill={color}
              rx="2"
              className="chart-bar"
              onMouseEnter={(e) => handleMouseEnter(item, e)}
              onMouseLeave={handleMouseLeave}
            />
            <text
              x={x + barWidth / 2}
              y="175"
              textAnchor="middle"
              className="chart-label"
            >
              {item.label.length > 6 ? item.label.substring(0, 6) + '...' : item.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// Pie Chart Component
const PieChart: React.FC<{ data: ChartDataPoint[], onHover: (data: TooltipData) => void }> = ({ data, onHover }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0) || 1;
  const chartRef = useRef<SVGSVGElement>(null);
  let currentAngle = -90;
  const [hoveredSlice, setHoveredSlice] = useState<number | null>(null);

  const handleMouseEnter = (item: ChartDataPoint, index: number, event: React.MouseEvent) => {
    setHoveredSlice(index);
    const rect = chartRef.current?.getBoundingClientRect();
    if (rect) {
      let x = event.clientX - rect.left;
      let y = event.clientY - rect.top;
      
      // Ensure tooltip stays within chart bounds
      const tooltipWidth = 140;
      
      // Keep x centered but within bounds
      if (x < tooltipWidth / 2) x = tooltipWidth / 2;
      if (x > rect.width - tooltipWidth / 2) x = rect.width - tooltipWidth / 2;
      
      // Keep y above cursor but visible
      const adjustedY = y - 70;
      
      onHover({
        x,
        y: adjustedY < 10 ? 10 : adjustedY,
        content: { ...item, meta: { percentage: `${((item.value / total) * 100).toFixed(1)}%` } },
        visible: true,
      });
    }
  };

  const handleMouseLeave = () => {
    setHoveredSlice(null);
    onHover({ x: 0, y: 0, content: { label: '', value: 0 }, visible: false });
  };

  return (
    <svg ref={chartRef} viewBox="0 0 200 200" className="chart-svg pie-chart">
      {data.slice(0, 6).map((item, index) => {
        const percentage = item.value / total;
        const angle = percentage * 360;
        const startAngle = currentAngle;
        currentAngle += angle;
        
        const startRad = (startAngle * Math.PI) / 180;
        const endRad = ((startAngle + angle) * Math.PI) / 180;
        
        const largeArcFlag = angle > 180 ? 1 : 0;
        const radius = hoveredSlice === index ? 72 : 70;
        const cx = 100;
        const cy = 100;
        
        const x1 = cx + radius * Math.cos(startRad);
        const y1 = cy + radius * Math.sin(startRad);
        const x2 = cx + radius * Math.cos(endRad);
        const y2 = cy + radius * Math.sin(endRad);
        
        const color = item.color || CHART_COLORS[index % CHART_COLORS.length];
        
        // Handle full circle case
        if (percentage >= 0.999) {
          return (
            <circle
              key={index}
              cx={cx}
              cy={cy}
              r={radius}
              fill={color}
              className="pie-slice"
              onMouseEnter={(e) => handleMouseEnter(item, index, e)}
              onMouseLeave={handleMouseLeave}
            />
          );
        }
        
        return (
          <path
            key={index}
            d={`M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
            fill={color}
            className={`pie-slice ${hoveredSlice === index ? 'hovered' : ''}`}
            onMouseEnter={(e) => handleMouseEnter(item, index, e)}
            onMouseLeave={handleMouseLeave}
          />
        );
      })}
      {/* Center circle for donut effect */}
      <circle cx="100" cy="100" r="40" className="pie-center-circle" />
      <text x="100" y="96" textAnchor="middle" dominantBaseline="middle" className="pie-center-text">
        {formatValue(total)}
      </text>
      <text x="100" y="112" textAnchor="middle" dominantBaseline="middle" className="pie-center-sub">
        total
      </text>
    </svg>
  );
};

// Line Chart Component
const LineChart: React.FC<{ data: ChartDataPoint[], onHover: (data: TooltipData) => void }> = ({ data, onHover }) => {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  const minValue = Math.min(...data.map(d => d.value), 0);
  const range = maxValue - minValue || 1;
  const chartRef = useRef<SVGSVGElement>(null);
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);

  const handleMouseEnter = (item: ChartDataPoint, index: number, event: React.MouseEvent) => {
    setHoveredPoint(index);
    const rect = chartRef.current?.getBoundingClientRect();
    if (rect) {
      let x = event.clientX - rect.left;
      let y = event.clientY - rect.top - 70;
      
      // Bounds checking
      const tooltipWidth = 140;
      if (x < tooltipWidth / 2) x = tooltipWidth / 2;
      if (x > rect.width - tooltipWidth / 2) x = rect.width - tooltipWidth / 2;
      if (y < 10) y = 10;
      
      onHover({
        x,
        y,
        content: item,
        visible: true,
      });
    }
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
    onHover({ x: 0, y: 0, content: { label: '', value: 0 }, visible: false });
  };

  const points = data.map((item, index) => {
    const x = 50 + (index / (data.length - 1 || 1)) * 230;
    const y = 150 - ((item.value - minValue) / range) * 130;
    return { x, y, item };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = linePath + ` L ${points[points.length - 1]?.x || 280} 150 L 50 150 Z`;

  const primaryColor = data[0]?.color || '#6366f1';

  // Calculate trend
  const firstValue = data[0]?.value || 0;
  const lastValue = data[data.length - 1]?.value || 0;
  const trend = lastValue - firstValue;
  const trendPercent = firstValue > 0 ? ((trend / firstValue) * 100).toFixed(1) : '0';

  return (
    <svg ref={chartRef} viewBox="0 0 300 180" className="chart-svg line-chart">
      {/* Gradient definition */}
      <defs>
        <linearGradient id={`lineGrad-${primaryColor.replace('#','')}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={primaryColor} stopOpacity="0.25" />
          <stop offset="100%" stopColor={primaryColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      
      {/* Y-axis gridlines */}
      {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
        <g key={i}>
          <line
            x1="50"
            y1={150 - ratio * 130}
            x2="280"
            y2={150 - ratio * 130}
            stroke="rgba(255,255,255,0.06)"
            strokeDasharray="4,4"
          />
          <text x="45" y={155 - ratio * 130} textAnchor="end" className="chart-axis-label">
            {formatValue(Math.round(minValue + range * ratio))}
          </text>
        </g>
      ))}
      
      {/* Area fill */}
      <path d={areaPath} fill={`url(#lineGrad-${primaryColor.replace('#','')})`} />
      
      {/* Line */}
      <path d={linePath} fill="none" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" />
      
      {/* Data points */}
      {points.map((p, index) => (
        <g key={index}>
          <circle
            cx={p.x}
            cy={p.y}
            r={hoveredPoint === index ? 6 : 4}
            fill={hoveredPoint === index ? primaryColor : primaryColor}
            stroke="white"
            strokeWidth="2"
            className="line-point"
            onMouseEnter={(e) => handleMouseEnter(p.item, index, e)}
            onMouseLeave={handleMouseLeave}
          />
          {index % Math.ceil(data.length / 6) === 0 && (
            <text x={p.x} y="170" textAnchor="middle" className="chart-label">
              {p.item.label.length > 5 ? p.item.label.substring(0, 5) : p.item.label}
            </text>
          )}
        </g>
      ))}
      
      {/* Trend indicator */}
      <text x="280" y="20" textAnchor="end" className={`trend-label ${trend >= 0 ? 'positive' : 'negative'}`}>
        {trend >= 0 ? '↑' : '↓'} {trendPercent}%
      </text>
    </svg>
  );
};

// Number Chart Component
const NumberChart: React.FC<{ data: ChartDataPoint[], trend?: ChartData['trend'] }> = ({ data, trend }) => {
  const mainValue = data[0]?.value || 0;
  const label = data[0]?.label || '';
  
  return (
    <div className="number-chart">
      <div className="number-value">{formatValue(mainValue)}</div>
      <div className="number-label">{label}</div>
      {trend && (
        <div className={`number-trend ${trend.direction}`}>
          <span className="trend-icon">
            {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'}
          </span>
          <span className="trend-value">{trend.value}%</span>
          <span className="trend-text">vs last period</span>
        </div>
      )}
    </div>
  );
};

// Chart Legend Component
const ChartLegend: React.FC<{ data: ChartDataPoint[] }> = ({ data }) => {
  return (
    <div className="chart-legend">
      {data.slice(0, 6).map((item, index) => (
        <div key={item.label} className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: item.color || CHART_COLORS[index % CHART_COLORS.length] }}
          />
          <span className="legend-label">{item.label}</span>
          <span className="legend-value">{formatValue(item.value)}</span>
        </div>
      ))}
    </div>
  );
};

// Data Table Component for expanded view
const DataTable: React.FC<{ data: ChartDataPoint[] }> = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  return (
    <div className="chart-data-table">
      <table>
        <thead>
          <tr>
            <th>Label</th>
            <th>Value</th>
            <th>Percentage</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={item.label}>
              <td>
                <span
                  className="table-color"
                  style={{ backgroundColor: item.color || CHART_COLORS[index % CHART_COLORS.length] }}
                />
                {item.label}
              </td>
              <td>{formatValue(item.value)}</td>
              <td>{((item.value / total) * 100).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td><strong>Total</strong></td>
            <td><strong>{formatValue(total)}</strong></td>
            <td><strong>100%</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// Interval Selector Component
const IntervalSelector: React.FC<{
  interval: TimeInterval;
  onChange: (interval: TimeInterval) => void;
}> = ({ interval, onChange }) => {
  return (
    <div className="interval-selector">
      {(['day', 'week', 'month', 'quarter', 'year'] as TimeInterval[]).map((int) => (
        <button
          key={int}
          className={`interval-btn ${interval === int ? 'active' : ''}`}
          onClick={() => onChange(int)}
        >
          {int.charAt(0).toUpperCase() + int.slice(1)}
        </button>
      ))}
    </div>
  );
};

// Period Navigator Component
const PeriodNavigator: React.FC<{
  currentPeriod?: Date;
  interval?: TimeInterval;
  onNavigate: (direction: 'prev' | 'next') => void;
}> = ({ currentPeriod, interval, onNavigate }) => {
  return (
    <div className="period-navigator">
      <button className="period-nav-btn" onClick={() => onNavigate('prev')}>
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>
      <span className="period-label">
        <FontAwesomeIcon icon={faCalendarAlt} className="period-icon" />
        {getPeriodLabel(currentPeriod, interval)}
      </span>
      <button className="period-nav-btn" onClick={() => onNavigate('next')}>
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
};

// Chart Detail Modal
const ChartDetail: React.FC<{
  chart: ChartData;
  onClose: () => void;
  onIntervalChange?: (id: string, interval: TimeInterval) => void;
  onPeriodChange?: (id: string, direction: 'prev' | 'next') => void;
}> = ({ chart, onClose, onIntervalChange, onPeriodChange }) => {
  const [tooltip, setTooltip] = useState<TooltipData>({
    x: 0, y: 0, content: { label: '', value: 0 }, visible: false
  });
  const [activeInterval, setActiveInterval] = useState<TimeInterval>(chart.interval || 'month');
  
  const handleIntervalChange = (interval: TimeInterval) => {
    setActiveInterval(interval);
    onIntervalChange?.(chart.id, interval);
  };

  const renderChart = () => {
    switch (chart.type) {
      case 'bar':
        return <BarChart data={chart.data} onHover={setTooltip} />;
      case 'pie':
        return <PieChart data={chart.data} onHover={setTooltip} />;
      case 'line':
        return <LineChart data={chart.data} onHover={setTooltip} />;
      case 'number':
        return <NumberChart data={chart.data} trend={chart.trend} />;
      default:
        return <div>Unsupported chart type</div>;
    }
  };

  return (
    <div className="chart-detail-overlay" onClick={onClose}>
      <div className="chart-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="chart-detail-header">
          <h2>{chart.title}</h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="chart-detail-controls">
          <IntervalSelector interval={activeInterval} onChange={handleIntervalChange} />
          <PeriodNavigator
            currentPeriod={chart.currentPeriod}
            interval={activeInterval}
            onNavigate={(dir) => onPeriodChange?.(chart.id, dir)}
          />
        </div>
        
        <div className="chart-detail-content">
          <div className="chart-detail-visualization">
            {renderChart()}
            <Tooltip data={tooltip} />
          </div>
          
          {chart.type !== 'number' && (
            <div className="chart-detail-sidebar">
              <h3>
                <FontAwesomeIcon icon={faInfoCircle} /> Data Breakdown
              </h3>
              <DataTable data={chart.data} />
            </div>
          )}
        </div>
        
        {chart.description && (
          <div className="chart-detail-description">
            <p>{chart.description}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Dashboard Chart Component
const DashboardChart: React.FC<DashboardChartProps> = ({
  chart,
  onDelete,
  onIntervalChange,
  onPeriodChange,
}) => {
  const [showDetail, setShowDetail] = useState(false);
  const [tooltip, setTooltip] = useState<TooltipData>({
    x: 0, y: 0, content: { label: '', value: 0 }, visible: false
  });

  const renderChart = () => {
    switch (chart.type) {
      case 'bar':
        return <BarChart data={chart.data} onHover={setTooltip} />;
      case 'pie':
        return <PieChart data={chart.data} onHover={setTooltip} />;
      case 'line':
        return <LineChart data={chart.data} onHover={setTooltip} />;
      case 'number':
        return <NumberChart data={chart.data} trend={chart.trend} />;
      default:
        return <div>Unsupported chart type</div>;
    }
  };

  return (
    <>
      <div className="dashboard-chart">
        <div className="chart-header">
          <h3 className="chart-title">{chart.title}</h3>
          <div className="chart-actions">
            <button
              className="chart-action-btn"
              onClick={() => setShowDetail(true)}
              title="Expand"
            >
              <FontAwesomeIcon icon={faExpand} />
            </button>
            {onDelete && (
              <button
                className="chart-action-btn delete"
                onClick={() => onDelete(chart.id)}
                title="Remove"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        </div>
        
        <div className="chart-body">
          {renderChart()}
          <Tooltip data={tooltip} />
        </div>
        
        {chart.type === 'pie' && chart.data.length > 0 && (
          <ChartLegend data={chart.data} />
        )}
        
        {chart.description && (
          <div className="chart-footer">
            <p className="chart-description">{chart.description}</p>
          </div>
        )}
      </div>

      {showDetail && (
        <ChartDetail
          chart={chart}
          onClose={() => setShowDetail(false)}
          onIntervalChange={onIntervalChange}
          onPeriodChange={onPeriodChange}
        />
      )}
    </>
  );
};

export default DashboardChart;
